import type { PurchaseHoldInternal, PurchaseHoldPublic, PurchasingHoldReason } from '../types/purchasing';

export const DEFAULT_HOLD_REASONS: PurchasingHoldReason[] = [
  { id: 'waiting_cash', label: 'Waiting for cash', visibleToRequesterDefault: false },
  { id: 'waiting_info', label: 'Waiting for information', visibleToRequesterDefault: true },
  { id: 'waiting_vendor', label: 'Waiting on vendor', visibleToRequesterDefault: true },
  { id: 'waiting_quote', label: 'Waiting for a better quote', visibleToRequesterDefault: false },
  { id: 'other', label: 'Other', visibleToRequesterDefault: false },
];

export function parseHoldReasons(raw: unknown): PurchasingHoldReason[] {
  if (!Array.isArray(raw)) return DEFAULT_HOLD_REASONS.map(r => ({ ...r }));
  const out: PurchasingHoldReason[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const id = typeof row.id === 'string' ? row.id.trim() : '';
    const label = typeof row.label === 'string' ? row.label.trim() : '';
    if (!id || !label || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      label,
      visibleToRequesterDefault: row.visibleToRequesterDefault !== false,
    });
  }
  return out.length ? out : DEFAULT_HOLD_REASONS.map(r => ({ ...r }));
}

export function parsePublicHold(raw: unknown): PurchaseHoldPublic | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  if (typeof row.visibleToRequester !== 'boolean') return undefined;
  const resumeStatus = typeof row.resumeStatus === 'string' ? row.resumeStatus : 'pending_approval';
  const publicLabel = typeof row.publicLabel === 'string' ? row.publicLabel.trim() : '';
  return {
    visibleToRequester: row.visibleToRequester,
    resumeStatus: resumeStatus as PurchaseHoldPublic['resumeStatus'],
    ...(row.visibleToRequester && publicLabel ? { publicLabel } : {}),
  };
}

export function parseInternalHold(raw: unknown): PurchaseHoldInternal | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  if (typeof row.reasonId !== 'string' || typeof row.heldById !== 'string') return undefined;
  const heldAt =
    raw && typeof (row.heldAt as { toDate?: () => Date })?.toDate === 'function'
      ? (row.heldAt as { toDate: () => Date }).toDate()
      : row.heldAt instanceof Date
        ? row.heldAt
        : new Date();
  return {
    reasonId: row.reasonId,
    reasonLabel: typeof row.reasonLabel === 'string' ? row.reasonLabel : row.reasonId,
    note: typeof row.note === 'string' && row.note.trim() ? row.note.trim() : undefined,
    visibleToRequester: row.visibleToRequester === true,
    heldById: row.heldById,
    heldByName: typeof row.heldByName === 'string' ? row.heldByName : '',
    heldAt,
  };
}

export function requesterHoldMessage(hold?: PurchaseHoldPublic | null): string {
  if (!hold) return '';
  if (hold.visibleToRequester && hold.publicLabel) return hold.publicLabel;
  return 'Your reviewer has paused this request.';
}

export function publicHoldLabel(reason: PurchasingHoldReason, note?: string): string {
  const extra = note?.trim();
  return extra ? `${reason.label} — ${extra}` : reason.label;
}
